import { inject, Injectable } from '@angular/core';

import { CustomerContact } from '../../../model/customer-information.model';
import { Customer } from '../../../model/customer.model';
import { ExtendedCustomer } from '../../../model/extended-customer.model';
import { GcdmBusinessPartner } from '../model/response.model';
import { GcdmExtendedCustomerViewModelMapper } from './extended-customer-model.mapper';

/**
 * Builds the profile page's `Customer` from an NSC Database (GCDM) business partner.
 *
 * GCDM owns the master data of a synced customer, but not the whole profile: the account number,
 * the vehicles, the dealer consents and the DMS ids exist only in Retail CRM. This mapper
 * therefore takes the Retail CRM record as the base and overlays every field GCDM owns, rather
 * than building a `Customer` from scratch - a field that GCDM does not model must keep Retail
 * CRM's value instead of silently becoming undefined.
 *
 * The field-level selection rules (newest communication wins, address matched on `preferredFor`,
 * market-dependent identification type) are not repeated here: they are shared with the sync
 * dialog and live in {@link GcdmExtendedCustomerViewModelMapper}.
 */
@Injectable({
  providedIn: 'root',
})
export class GcdmCustomerViewModelMapper {
  private readonly extendedCustomerMapper = inject(GcdmExtendedCustomerViewModelMapper);

  /**
   * @param businessPartner the record stored in the NSC Database
   * @param retailCustomer  the same customer as Retail CRM holds it, for the fields GCDM does not own
   */
  mapToCustomer(businessPartner: GcdmBusinessPartner, retailCustomer: Customer): Customer {
    const gcdmData: ExtendedCustomer = this.extendedCustomerMapper.mapToExtendedCustomer(businessPartner);

    return {
      // --- Retail CRM's, regardless of the sync state ---
      accountNumber: retailCustomer.accountNumber,
      dmsIds: retailCustomer.dmsIds,
      vehicles: retailCustomer.vehicles,
      dealerConsent: retailCustomer.dealerConsent,
      inSyncWithDirectSales: retailCustomer.inSyncWithDirectSales,

      // --- GCDM's ---
      // The GCID is the key we just read by; Retail CRM's copy of it can only be older.
      gcid: businessPartner.gcid ?? retailCustomer.gcid,
      // GCDM's login mail is the BMW/MINI ID. It gates the email field of the edit dialog, so
      // reading it from the mirror would let the user edit an email that is actually locked.
      bmwMiniId: businessPartner.loginIdMail ?? retailCustomer.bmwMiniId,
      firstName: gcdmData.firstName,
      middleName: gcdmData.middleName,
      lastName: gcdmData.lastName,
      suffix: gcdmData.suffix,
      correspondenceLanguage: gcdmData.correspondenceLanguage,
      salutation: gcdmData.salutation,
      title: gcdmData.title,
      privateContact: this.mapContact(gcdmData, 'private'),
      businessContact: this.mapContact(gcdmData, 'business'),
      taxNumber: gcdmData.taxNumber,
      taxNumberCountry: gcdmData.taxNumberCountry,
      nationalId1: gcdmData.nationalId1,
      nationalId1DocumentType: gcdmData.nationalId1DocumentType,
    };
  }

  /**
   * `Customer` groups communication by category, `ExtendedCustomer` by field. `bmwMiniId` is
   * deliberately not repeated per contact: it is a single login, not a private/business pair.
   */
  private mapContact(data: ExtendedCustomer, category: 'private' | 'business'): CustomerContact {
    return category === 'private'
      ? {
          address: data.privateAddress,
          email: data.emailPrivate,
          phone: data.phonePrivate,
          mobile: data.mobilePrivate,
        }
      : {
          address: data.businessAddress,
          email: data.emailBusiness,
          phone: data.phoneBusiness,
          mobile: data.mobileBusiness,
        };
  }
}
